import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AiUsageSourceRow } from '@bliks-io/contracts';
import { Badge, DataTable, createColumnHeader, type DataTableColumnDef } from '@bliks-io/ui';
import { formatMicroEur, formatNumber } from '@/lib/formatters';
import { getAiModelLabel } from '@/pages/admin/ai-model-labels';

interface AiUsageSourceTableProps {
  sources: AiUsageSourceRow[];
  isLoading: boolean;
}

/** Per-source × model breakdown, shared by the AI usage drill-down and the
 *  tenant detail Usage tab. */
export function AiUsageSourceTable({ sources, isLoading }: AiUsageSourceTableProps) {
  const { t, i18n } = useTranslation();

  const columns = useMemo<DataTableColumnDef<AiUsageSourceRow>[]>(
    () => [
      {
        accessorKey: 'sourceLabel',
        header: createColumnHeader(t('aiUsage.source')),
        cell: ({ row }) => (
          <span className="font-medium">
            {row.original.sourceLabel || t('aiUsage.unknownSource')}
          </span>
        )
      },
      {
        accessorKey: 'sourceKind',
        header: createColumnHeader(t('aiUsage.kind')),
        cell: ({ row }) => (
          <Badge variant="outline">
            {row.original.sourceKind === 'feature'
              ? t('aiUsage.kindFeature')
              : t('aiUsage.kindApp')}
          </Badge>
        )
      },
      {
        accessorKey: 'model',
        header: createColumnHeader(t('aiUsage.model')),
        cell: ({ row }) => getAiModelLabel(row.original.model)
      },
      {
        accessorKey: 'eventCount',
        header: createColumnHeader(t('aiUsage.calls')),
        cell: ({ row }) => formatNumber(row.original.eventCount, i18n.language)
      },
      {
        accessorKey: 'inputTokens',
        header: createColumnHeader(t('aiUsage.inputTokens')),
        cell: ({ row }) => formatNumber(row.original.inputTokens, i18n.language)
      },
      {
        accessorKey: 'outputTokens',
        header: createColumnHeader(t('aiUsage.outputTokens')),
        cell: ({ row }) => formatNumber(row.original.outputTokens, i18n.language)
      },
      {
        accessorKey: 'totalCostMicroEur',
        header: createColumnHeader(t('aiUsage.cost')),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {formatMicroEur(row.original.totalCostMicroEur, i18n.language)}
            {row.original.priceMissingCount > 0 && (
              <span
                className="ml-1 text-warning"
                title={t('aiUsage.priceMissingEvents', {
                  count: row.original.priceMissingCount
                })}
              >
                *
              </span>
            )}
          </span>
        )
      }
    ],
    [t, i18n.language]
  );

  return (
    <DataTable
      columns={columns}
      data={sources}
      isLoading={isLoading}
      enableSorting
      tableId="aiUsageSources"
      emptyMessage={t('aiUsage.empty')}
    />
  );
}
